import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchImageAsBlob } from '../services/api';
import { loadAnnotationStyle, saveAnnotationStyle, strokeWidthFor } from '../utils/annotationStyle';

function dataUrlToBlob(dataUrl) {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// A phone photo is ~4000 px wide; rasterised to PNG at that size it weighed
// 8–10 MB, which is what made saving an annotation slow on a tablet and the
// PDF heavy. 2560 px JPEG keeps every annotation legible at ~0.5 MB.
export const RENDER_MAX_SIDE = 2560;
const RENDER_QUALITY = 0.85;

export function renderSize(naturalWidth, naturalHeight, maxSide = RENDER_MAX_SIDE) {
  const scale = Math.min(1, maxSide / Math.max(naturalWidth, naturalHeight));
  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale),
  };
}

// The tools used on almost every photo, one tap away instead of hidden in the
// toolbar's drop-downs (the plain line lives under "Arrow" there).
const QUICK_TOOLS = [
  { type: 'ArrowMarker', key: 'toolArrow' },
  { type: 'LineMarker', key: 'toolLine' },
  { type: 'EllipseFrameMarker', key: 'toolEllipse' },
  { type: 'FrameMarker', key: 'toolRectangle' },
  { type: 'TextMarker', key: 'toolText' },
];

/**
 * Full-screen annotation editor (@markerjs/markerjs-ui AnnotationEditor).
 *
 * It is laid over the wizard rather than replacing it, so closing it returns
 * the expert to the exact photo they were on, without reloading every image.
 */
export default function ImageAnnotator({ reportId, filename, existingState, title, onSave, onCancel }) {
  const { t } = useTranslation();
  const editorDivRef = useRef(null);
  const editorRef = useRef(null);
  const imgRef = useRef(null);
  const repeatRef = useRef(false);
  const savingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [repeat, setRepeat] = useState(false);
  repeatRef.current = repeat;

  // Callbacks change identity on every parent render; reading them through refs
  // keeps the editor from being torn down (and the drawing lost) mid-session.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const existingStateRef = useRef(existingState);
  const tRef = useRef(t);
  tRef.current = t;

  // The page underneath must not scroll behind the overlay.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const editorDiv = editorDivRef.current;
    let resizeObserver = null;

    async function initEditor() {
      const { AnnotationEditor } = await import('@markerjs/markerjs-ui');
      if (cancelled || !editorDiv) return;

      const img = document.createElement('img');
      img.src = await fetchImageAsBlob(reportId, filename);
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
      if (cancelled || !editorDiv) return;

      const editor = new AnnotationEditor();
      editor.targetImage = img;
      // Rendering is ours (handleSave), so both save buttons produce the same file.
      editor.settings.renderOnSave = false;
      // Open the photo whole, fitted to the screen, instead of at 100 %.
      editor.settings.autoZoomIn = true;
      editor.settings.autoZoomOut = true;
      editor.style.display = 'block';
      editor.style.width = '100%';
      editor.style.height = '100%';

      const markerArea = editor.markerArea;
      const naturalWidth = img.naturalWidth || 1600;

      // New markers start with the last colour / thickness used, not red 3 px.
      markerArea.addEventListener('markercreating', (e) => {
        const ed = e.detail.markerEditor;
        const style = loadAnnotationStyle();
        try {
          if (ed.marker?.typeName === 'TextMarker') {
            ed.color = style.color;
          } else {
            ed.strokeColor = style.color;
            ed.strokeWidth = strokeWidthFor(style, naturalWidth);
          }
        } catch {
          // a marker type without stroke: keep its own defaults
        }
      });
      const remember = (e) => {
        const ed = e.detail.markerEditor;
        if (!ed || ed.marker?.typeName === 'TextMarker') return;
        saveAnnotationStyle({ color: ed.strokeColor, width: ed.strokeWidth }, naturalWidth);
      };
      markerArea.addEventListener('markerchange', remember);
      markerArea.addEventListener('markercreate', (e) => {
        remember(e);
        // "Repeat": the same tool is armed again, so ten arrows take ten drags.
        if (repeatRef.current) {
          markerArea.createMarker(e.detail.markerEditor.marker.typeName);
        }
      });

      editor.addEventListener('editorsave', (event) => handleSave(event.detail.state));
      editor.addEventListener('editorclose', () => onCancelRef.current());

      editorDiv.appendChild(editor);

      if (existingStateRef.current) {
        try {
          editor.restoreState(existingStateRef.current);
        } catch {
          // ignore restore errors
        }
      }

      editorRef.current = editor;
      imgRef.current = img;
      setReady(true);

      // Refit once laid out, and again when the tablet is rotated.
      requestAnimationFrame(() => markerArea.autoZoom());
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => markerArea.autoZoom());
        resizeObserver.observe(editorDiv);
      }
    }

    initEditor();

    return () => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();
      if (editorRef.current && editorDiv) {
        try {
          editorDiv.removeChild(editorRef.current);
        } catch {
          // already removed
        }
        editorRef.current = null;
      }
      if (imgRef.current && imgRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(imgRef.current.src);
      }
    };
    // handleSave only reads refs; re-running on its identity would reset the editor.
  }, [reportId, filename]);

  async function rasterize(state) {
    const { Renderer } = await import('@markerjs/markerjs3');
    const img = imgRef.current;
    const renderer = new Renderer();
    renderer.targetImage = img;
    renderer.naturalSize = false;
    const { width, height } = renderSize(img.naturalWidth, img.naturalHeight);
    renderer.width = width;
    renderer.height = height;
    renderer.imageType = 'image/jpeg';
    renderer.imageQuality = RENDER_QUALITY;
    return dataUrlToBlob(await renderer.rasterize(state));
  }

  async function handleSave(stateArg) {
    const editor = editorRef.current;
    if (!editor || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const state = stateArg || editor.markerArea.getState();
      const blob = await rasterize(state);
      await onSaveRef.current(blob, state);
    } catch (err) {
      console.error('Annotation save failed:', err);
      setError(tRef.current('wizard.saveFailed'));
      savingRef.current = false;
      setSaving(false);
    }
  }

  function pickTool(type) {
    const markerArea = editorRef.current?.markerArea;
    if (markerArea) markerArea.createMarker(type);
  }

  return (
    <div className="annotator-overlay" role="dialog" aria-modal="true" aria-label={t('wizard.annotate')}>
      <div className="annotator-bar">
        <h3 className="annotator-title">{title || t('wizard.annotate')}</h3>
        {ready && (
          <div className="annotator-tools" role="toolbar" aria-label={t('annotate.tools')}>
            {QUICK_TOOLS.map((tool) => (
              <button
                key={tool.type}
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => pickTool(tool.type)}
              >
                {t(`annotate.${tool.key}`)}
              </button>
            ))}
            <label className="annotator-repeat" title={t('annotate.repeatHint')}>
              <input
                type="checkbox"
                checked={repeat}
                onChange={(e) => setRepeat(e.target.checked)}
              />
              {t('annotate.repeat')}
            </label>
          </div>
        )}
        <div className="annotator-actions">
          {error && <span className="ai-error">{error}</span>}
          {ready && (
            <button className="btn btn-primary" onClick={() => handleSave()} disabled={saving}>
              {saving ? t('annotate.saving') : t('wizard.save')}
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => onCancelRef.current()} disabled={saving}>
            {t('annotate.cancel')}
          </button>
        </div>
      </div>

      {!ready && <div className="annotator-loading">{t('auth.loading')}</div>}

      {/* Kept laid out (not display:none) while loading: markerjs measures this
          box to fit the photo, and a zero-width box made the fit silently fail. */}
      <div
        ref={editorDivRef}
        data-testid="annotation-editor-container"
        className="annotator-canvas"
        style={{ visibility: ready ? 'visible' : 'hidden' }}
      />
    </div>
  );
}
